import { cn } from '@labflow/ui';

/**
 * 이니셜 아바타 — D3 §4.6 `Avatar`의 최소 구현. 사진을 저장하지 않아 이니셜만 그린다(S22 §2).
 * `packages/ui`에 `Avatar`가 생기면 같은 props로 교체한다.
 */
export interface AvatarProps {
  name: string;
  /** @default 'sm' — 24px · `md` 32px */
  size?: 'sm' | 'md';
  /** 옆에 이름 글자가 있으면 보조기술에서 숨긴다. */
  decorative?: boolean;
  className?: string;
}

const LATIN = /^[A-Za-z]/;

/** 한글 1자 · 라틴 2자 (D3 §4.6). */
export function avatarInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  if (LATIN.test(trimmed)) {
    const words = trimmed.split(/\s+/);
    const letters = words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : trimmed.slice(0, 2);
    return letters.toUpperCase();
  }
  return Array.from(trimmed)[0]!;
}

export function Avatar({ name, size = 'sm', decorative = false, className }: AvatarProps) {
  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative ? true : undefined}
      className={cn(
        'bg-brand-soft text-brand-600 inline-flex shrink-0 items-center justify-center rounded-full font-medium select-none',
        size === 'sm' ? 'size-6 text-xs' : 'size-8 text-sm',
        className,
      )}
    >
      {avatarInitials(name)}
    </span>
  );
}
